import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import InputText from 'components/Form/InputText'
import { useFormikContext } from 'formik'
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react'
import Checkbox from 'components/Form/Checkbox'
import LabelInput from 'components/Form/LabelInput'
import { Wrapper } from '../../../RolesForm/Partials/CtcJoblocation/Style/style'
import {
  EmptyDivElement,
  FlexCell,
  IndentedTd,
  LabelText,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledThead,
  TableContainer,
} from '../../style/style'
import { ExpandIcon, TableCheckbox } from './style'
import { flatten } from 'helpers/formHelpers'
import { debounce } from 'utils/debounce'
import { setSpecialisationList } from '../../../../../actions'
import { buildEligibilityPayload } from '../../../../../../../helpers/formHelpers'

const CourseTable = React.memo(
  ({
    data,
    setCourseData,
    handleSearch,
    degreeLoading,
    disabled,
    selectedCourses,
    setSelectedCourses,
    setIsAllSelected,
    isAllSelected,
    search,
    criteria,
    setSearch,
    setSelectedDegreeTypes,
    selectedDegreeTypes,
    active, // New prop to control visibility
    localSearch,
    setLocalSearch,
    expandedDegrees,
    setExpandedDegrees,
    expandedStreams,
    setExpandedStreams,
  }) => {
    const { values, setFieldValue } = useFormikContext()

    // Helper function to display labels
    const getQualificationLabel = (qualification) => {
      return qualification === 'DIPLOMA' ? 'DIPLOMA / ITI' : qualification
    }
   

    // Reset expansion states when table becomes inactive
    useEffect(() => {
      if (!active) {
        setExpandedDegrees([])
        setExpandedStreams([])
      }
    }, [active])

    const getSpecs = useCallback(
      stream => stream.specializations || stream.specialisations || [],
      []
    )

    const toggleExpand = useCallback(
      (setState, id) =>
        setState(prev =>
          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        ),
      []
    )

    const updateSelectedCourses = useCallback(
      updater => {
        setSelectedCourses(prev => {
          const prevArray = Array.isArray(prev) ? [...prev] : []
          const currentCriteria = prevArray[criteria] || []
          const updated =
            typeof updater === 'function' ? updater(currentCriteria) : updater
          prevArray[criteria] = Array.isArray(updated) ? [...updated] : []

          const eligibilityData = buildEligibilityPayload(prevArray[criteria])
          setFieldValue(
            `elgibilty.eligibilityCriteria[${criteria}]`,
            eligibilityData
          )

          return [...prevArray]
        })
      },
      [
        setSelectedCourses,
        setFieldValue,
        criteria,
        values.elgibilty.eligibilityCriteria,
      ]
    )

    const handleDegreeChange = useCallback(
      (degreeObj, checked) => {
        updateSelectedCourses(prev => {
          const currentCriteria = prev || []
          const exists =
            Array.isArray(currentCriteria) &&
            currentCriteria.some(d => d.degreeId === degreeObj.degreeId)
          const newSelection = checked
            ? exists
              ? currentCriteria.map(d =>
                  d.degreeId === degreeObj.degreeId
                    ? { ...d, isSelected: true }
                    : d
                )
              : [
                  ...currentCriteria,
                  {
                    ...degreeObj,
                    isSelected: true,
                    streams: degreeObj.streams || [],
                  },
                ]
            : currentCriteria
                .map(d =>
                  d.degreeId === degreeObj.degreeId
                    ? { ...d, isSelected: false }
                    : d
                )
                .filter(d => d.isSelected || d.degreeId !== degreeObj.degreeId)
          return newSelection
        })
      },
      [updateSelectedCourses, criteria]
    )

    const handleStreamChange = useCallback(
      (degreeId, streamId) => {
        updateSelectedCourses(prev => {
          const currentCriteria = prev || []

          // Check if this degree+stream already exists
          const degreeIndex = currentCriteria.findIndex(
            d =>
              d.degreeId === degreeId &&
              d.streams.some(s => s.streamId === streamId)
          )

          if (degreeIndex === -1) {
            // Add new degree+stream entry
            const degree = data.find(d => d.degreeId === degreeId)
            if (!degree) return currentCriteria

            const stream = (degree.streams || []).find(
              s => s.streamId === streamId
            )
            if (!stream) return currentCriteria

            const newStream = {
              ...stream,
              isSelected: true,
              specializations: getSpecs(stream).map(sp => ({
                ...sp,
                isSelected: false,
              })),
            }

            return [
              ...currentCriteria,
              {
                ...degree,
                isSelected: true,
                streams: [newStream],
              },
            ]
          }

          // If found → toggle that stream
          const newSelection = currentCriteria.map((deg, idx) => {
            if (idx !== degreeIndex) return deg

            const newStreams = deg.streams.map(stream => {
              if (stream.streamId !== streamId) return stream

              const isChecking = !stream.isSelected
              return {
                ...stream,
                isSelected: isChecking,
                specializations: (
                  stream.specializations || getSpecs(stream)
                ).map(sp => ({
                  ...sp,
                  isSelected: isChecking ? sp.isSelected : false,
                })),
              }
            })

            // Always keep the degree selected
            return {
              ...deg,
              isSelected: true,
              streams: newStreams,
            }
          })

          return newSelection
        })
      },
      [updateSelectedCourses, data, getSpecs]
    )

    const handleSpecChange = useCallback(
      (degreeId, streamId, specId, currentStream) => {
        updateSelectedCourses(prev => {
          const currentCriteria = prev || []

          const degreeIndex = currentCriteria.findIndex(
            d =>
              d.degreeId === degreeId &&
              d.streams.some(s => s.streamId === streamId)
          )

          if (degreeIndex === -1) {
            const degree = data.find(d => d.degreeId === degreeId)
            if (!degree) return currentCriteria

            const stream = (degree.streams || []).find(
              s => s.streamId === streamId
            )
            if (!stream) return currentCriteria

            const newSpecs = getSpecs(stream).map(spec => ({
              ...spec,
              isSelected:
                spec.id === specId || spec.specialisationId === specId,
            }))

            return [
              ...currentCriteria,
              {
                ...degree,
                isSelected: true,
                streams: [
                  { ...stream, isSelected: true, specializations: newSpecs },
                ],
              },
            ]
          }

          // If degree+stream exists → toggle spec
          const newSelection = currentCriteria.map((deg, idx) => {
            if (idx !== degreeIndex) return deg

            const newStreams = deg.streams.map(stream => {
              if (stream.streamId !== streamId) return stream

              const updatedSpecs = [
                ...(stream.specializations || getSpecs(stream)),
                ...(currentStream?.specialisations || []),
              ].reduce((acc, spec) => {
                const specIdToCheck = spec.id || spec.specialisationId
                const existingSpec = acc.find(
                  s =>
                    s.id === specIdToCheck ||
                    s.specialisationId === specIdToCheck
                )

                if (!existingSpec) {
                  acc.push({
                    ...spec,
                    isSelected:
                      spec.id === specId || spec.specialisationId === specId
                        ? !spec.isSelected
                        : spec.isSelected || false,
                  })
                }
                return acc
              }, [])
              return {
                ...stream,
                isSelected: true,
                specializations: updatedSpecs,
              }
            })

            return {
              ...deg,
              isSelected: true,
              streams: newStreams,
            }
          })

          return newSelection
        })
      },
      [updateSelectedCourses, data, getSpecs]
    )

    const handleAllDegrees = useCallback(
  checked => {
    updateSelectedCourses(() => {
      if (checked) {
        // Select all degrees and mark them as selected
        return data.map(degree => ({
          ...degree,
          isSelected: true,
          streams: (degree.streams || []).map(stream => ({
            ...stream,
            isSelected: false,
            specializations: getSpecs(stream).map(spec => ({
              ...spec,
              isSelected: false,
            })),
          })),
        }))
      } else {
        // Uncheck all degrees of this degree type (not all globally)
        return []
      }
    })
  },
  [updateSelectedCourses, data, getSpecs]
)

    const handleAllDepartments = useCallback(
      checked => {
        updateSelectedCourses(prev => {
          const currentCriteria = prev || []
          const currentTypeDegreeIds = new Set(data.map(d => d.degreeId))

          // For unchecking, only uncheck streams, keep degrees
          if (!checked) {
            return (prev || []).map(degree => {
              if (!currentTypeDegreeIds.has(degree.degreeId)) {
                return degree // Return unchanged if not in current type
              }
              return {
                ...degree,
                streams: (degree.streams || []).map(stream => ({
                  ...stream,
                  isSelected: false,
                  specializations: (
                    stream.specializations ||
                    getSpecs(stream) ||
                    []
                  ).map(spec => ({
                    ...spec,
                    isSelected: false,
                  })),
                })),
              }
            })
          }

          // For checking, add streams for current type
          const updatedData = data.map(degree => {
            const existingDegree =
              currentCriteria.find(d => d.degreeId === degree.degreeId) || {}
            return {
              ...existingDegree,
              ...degree,
              isSelected: true,
              streams: (degree.streams || []).map(stream => ({
                ...stream,
                isSelected: true,
                specializations: getSpecs(stream).map(spec => ({
                  ...spec,
                  isSelected: false,
                })),
              })),
            }
          })

          // Combine with other degrees
          const otherDegrees = (prev || []).filter(
            degree => !currentTypeDegreeIds.has(degree.degreeId)
          )
          return [...otherDegrees, ...updatedData]
        })

        if (checked) {
          setExpandedDegrees(data.map(degree => degree.degreeId))
        } else {
          setExpandedDegrees(prev =>
            prev.filter(id => !data.some(d => d.degreeId === id))
          )
        }
      },
      [updateSelectedCourses, data, getSpecs]
    )
    const handleAllSpecializations = useCallback(
      checked => {
        updateSelectedCourses(prev => {
          const currentCriteria = prev || []
          const currentTypeDegreeIds = new Set(data.map(d => d.degreeId))

          // For unchecking, only uncheck specializations, keep degrees and streams
          if (!checked) {
            return (prev || []).map(degree => {
              if (!currentTypeDegreeIds.has(degree.degreeId)) {
                return degree // Return unchanged if not in current type
              }
              return {
                ...degree,
                streams: (degree.streams || []).map(stream => {
                  const specs = stream.specializations || getSpecs(stream) || []
                  if (specs.length === 0) {
                    return stream // Return unchanged if no specializations
                  }
                  return {
                    ...stream,
                    specializations: specs.map(spec => ({
                      ...spec,
                      isSelected: false,
                    })),
                  }
                }),
              }
            })
          }
          const areAllSelected = currentCriteria.every(
            degree =>
              degree.isSelected === true &&
              (degree.streams || []).every(stream => stream.isSelected === true)
          )

          // For checking, set all specializations to selected
          const updatedData = data.map(degree => {
            const existingDegree =
              currentCriteria.find(d => d.degreeId === degree.degreeId) || {}
            return {
              ...existingDegree,
              ...degree,
              isSelected: true,
              streams: (degree.streams || []).map(stream => {
                const existingStream =
                  existingDegree.streams?.find(
                    s => s.streamId === stream.streamId
                  ) || {}
                const specs = getSpecs(stream)
                return {
                  ...existingStream,
                  ...stream,
                  isSelected: areAllSelected ? true : specs.length > 0, // Select stream only if it has specializations
                  specializations: specs.map(spec => ({
                    ...spec,
                    isSelected: true,
                  })),
                }
              }),
            }
          })

          // Combine with other degrees
          //const otherDegrees = prev || []
          const otherDegrees = (prev || []).filter(
            degree => !currentTypeDegreeIds.has(degree.degreeId)
          )

          return [...otherDegrees, ...updatedData]
        })

        if (checked) {
          // Expand all degrees and streams that have specializations
          const degreesWithSpecs = data.filter(degree =>
            (degree.streams || []).some(
              stream =>
                (stream.specializations || getSpecs(stream) || []).length > 0
            )
          )
          setExpandedDegrees(degreesWithSpecs.map(degree => degree.degreeId))
          setExpandedStreams(
            degreesWithSpecs.flatMap(degree =>
              (degree.streams || [])
                .filter(
                  stream =>
                    (stream.specializations || getSpecs(stream) || []).length >
                    0
                )
                .map(stream => stream.streamId)
            )
          )
        } else {
          // Only collapse the current type's degrees and streams
          // setExpandedDegrees(prev =>
          //   prev.filter(id => !data.some(d => d.degreeId === id))
          // )
          setExpandedStreams(prev =>
            prev.filter(
              streamId =>
                !data.some(degree =>
                  (degree.streams || []).some(
                    stream => stream.streamId === streamId
                  )
                )
            )
          )
        }
      },
      [updateSelectedCourses, data, getSpecs]
    )

    const handleSelectAll = useCallback(
      e => {
        const degreeType = values.elgibilty.qualification[criteria]

        if (degreeType) {
          setSelectedCourses(prev => {
            const newArray = Array.isArray(prev) ? [...prev] : []
            const currentCriteria = Array.isArray(newArray[criteria])
              ? [...newArray[criteria]]
              : []

            if (e.target.checked) {
              const updatedCriteria = currentCriteria.filter(
                item => (item.degreeType || item.type) !== degreeType
              )
              // Add the new item
              updatedCriteria.push({
                isAllDegree: true,
                degreeType,
              })

              // Build the payload with all items that have isAllDegree or different degreeType
              const payload = buildEligibilityPayload(updatedCriteria)

              // Update formik field with the full payload
              setFieldValue(
                `elgibilty.eligibilityCriteria[${criteria}]`,
                payload
              )

              // Update selected courses with the updated criteria
              newArray[criteria] = updatedCriteria
            } else {
              setExpandedDegrees([])
              setExpandedStreams([])
              // When unchecking, filter out items with this degreeType
              const filteredCriteria = currentCriteria.filter(
                item => (item.degreeType || item.type) !== degreeType
              )

              // Update formik field
              setFieldValue(
                `elgibilty.eligibilityCriteria[${criteria}]`,
                buildEligibilityPayload(filteredCriteria)
              )

              // Update selected courses
              newArray[criteria] = filteredCriteria
            }

            return newArray
          })
        }
      },
      [
        criteria,
        values.elgibilty.qualification,
        setFieldValue,
        buildEligibilityPayload,
      ]
    )
    const {
      allDegrees,
      degreesIndeterminate,
      allStreams,
      streamsIndeterminate,
      allSpecs,
      specsIndeterminate,
    } = useMemo(() => {
      const currentType = values.elgibilty.qualification[criteria]
      const currentTypeData = data.filter(d => d.type === currentType)
      const selected = selectedCourses?.[criteria] || []

      // Get all degree IDs for current type
      const allDegreeIds = new Set(currentTypeData.map(d => d.degreeId))
      const selectedDegreeIds = new Set(
        selected.filter(d => d.isSelected).map(d => d.degreeId)
      )

      // Calculate degree states
      const allDeg =
        allDegreeIds.size > 0 &&
        Array.from(allDegreeIds).every(id => selectedDegreeIds.has(id))
      const someDeg = selected.some(d => d.isSelected)
      const degIndeterminate = someDeg && !allDeg

      let allStr = false
      let someStr = false
      let allSp = true // Start with true, will be set to false if any spec is not selected
      let someSp = false
      let totalSpecs = 0
      let selectedSpecs = 0

      // First pass: count all specializations
      currentTypeData.forEach(degree => {
        ;(degree.streams || []).forEach(stream => {
          const specs = getSpecs(stream)
          totalSpecs += specs.length
        })
      })

      // Second pass: count selected specializations and determine states
      selected.forEach(degree => {
        ;(degree.streams || []).forEach(stream => {
          const specs = getSpecs(stream)
          const selectedInStream = specs.filter(s => s.isSelected)

          const degreeInData = currentTypeData.find(
            d => d.degreeId === degree.degreeId
          )
          if (!degreeInData) return

          const degreeStreams = degreeInData.streams || []
          const selectedStreams = degree.streams || []

          // Check if all streams are selected for this degree
          const allStreamsSelected = degreeStreams.every(stream =>
            selectedStreams.some(
              s => s.streamId === stream.streamId && s.isSelected
            )
          )

          if (!allStreamsSelected) {
            allStr = false
          } else {
            allStr = true
          }

          if (stream?.isSelected) {
            someStr = true
            selectedSpecs += selectedInStream.length

            // // If any spec in a selected stream is not selected, then not all specs are selected
            // if (selectedInStream.length < specs.length) {
            //   console.log(selectedInStream.length,specs.length,'lenth')
            //   allSp = false
            // }
          } else {
            allStr = false
            // If any spec in a non-selected stream is selected, we have someSp
            if (selectedInStream.length > 0) {
              someSp = true
              allSp = false
            }
          }
        })
      })

      // Final check for allSp - must have at least one spec and all must be selected
      allSp = allSp && totalSpecs > 0 && selectedSpecs === totalSpecs
      if (selected.length === 0) {
        allStr = false
        allSp = false
      }
      if (totalSpecs === 0) {
        allSp = allStr
        someSp = someStr
      }

      // Set someSp if we have any selected specs but not all
      someSp = selectedSpecs > 0 && selectedSpecs < totalSpecs

      const strIndeterminate = someStr && !allStr
      const spIndeterminate = someSp && !allSp

      return {
        allDegrees: allDeg,
        degreesIndeterminate: degIndeterminate,
        allStreams: allStr,
        streamsIndeterminate: strIndeterminate,
        allSpecs: allSp,
        specsIndeterminate: spIndeterminate,
      }
    }, [
      selectedCourses,
      criteria,
      data,
      values.elgibilty.qualification,
      getSpecs,
    ])

    const isDegreeSelected = useCallback(
      degreeId => {
        return (selectedCourses?.[criteria] || []).some(
          degree => degree.degreeId === degreeId && degree.isSelected
        )
      },
      [selectedCourses, criteria]
    )

    const isStreamSelected = useCallback(
      (degreeId, streamId) => {
        return (selectedCourses?.[criteria] || []).some(
          degree =>
            degree.degreeId === degreeId &&
            (degree.streams || []).some(
              stream => stream.streamId === streamId && stream.isSelected
            )
        )
      },
      [selectedCourses, criteria]
    )

    const isSpecSelected = useCallback(
      (degreeId, streamId, specId) => {
        return (selectedCourses?.[criteria] || []).some(
          degree =>
            degree.degreeId === degreeId &&
            (degree.streams || []).some(
              stream =>
                stream.streamId === streamId &&
                (stream.specializations || stream.specialisations || []).some(
                  spec =>
                    (spec.id === specId || spec.specialisationId === specId) &&
                    spec.isSelected
                )
            )
        )
      },
      [selectedCourses, criteria]
    )

    const isDegreeTypeSelected = useCallback(
      degreeType => {
        return (
          Array.isArray(values.elgibilty?.eligibilityCriteria?.[criteria]) &&
          values.elgibilty.eligibilityCriteria[criteria]?.some(
            item => item?.degreeType === degreeType && item?.isAllDegree
          ) || false
        )
      },
      [values.elgibilty.eligibilityCriteria, criteria]
    )

    const handleDegreeSearch = useCallback(
      value => {
        setLocalSearch(value)
        handleSearch(value, 'degree')
      },
      [handleSearch]
    )

    useEffect(() => {
      // Skip if not active or no selected courses
      if (!active || !Array.isArray(selectedCourses)) {
        return
      }

      // Get the courses for the current criteria
      const criteriaCourses = selectedCourses[criteria] || []

      // Get unique degree types from selected courses
      const degreeTypes = []
      const seen = new Set()

      criteriaCourses.forEach(course => {
        const type = course.degreeType || course.type
        if (type && course.isSelected !== false && !seen.has(type)) {
          seen.add(type)
          degreeTypes.push(type)
        }
      })

      // Update the state directly with the new degree types
      setSelectedDegreeTypes(prev => {
        const newState = [...(prev || [])]
        newState[criteria] = degreeTypes
        return newState
      })
    }, [selectedCourses, criteria, values?.elgibilty?.qualification, active])

    // Early return after all hooks have been called
    if (!active) return null

    const onClickClearAll = criteria => {
      if (disabled) return
      // clear the UI state
      setSelectedCourses(prev =>
        prev.map((item, idx) => (idx === criteria ? [] : item))
      )

      // clear the form field too
      setFieldValue(
        `elgibilty.eligibilityCriteria[${criteria}]`,
        buildEligibilityPayload([]) // pass an empty array to reset eligibilityData
      )
    }

    return (
      <>
        <Wrapper>
          <Checkbox
            checked={isDegreeTypeSelected(
              values.elgibilty.qualification[criteria]
            )}
            isCustom
            onChange={handleSelectAll}
            label={`Select all degrees under ${
              getQualificationLabel(values.elgibilty.qualification[criteria]) || 'Unknown'
            }`}
            name={`elgibilty.allSelected${criteria}`}
            disabled={disabled}
          />
        </Wrapper>
        {!isDegreeTypeSelected(values.elgibilty.qualification[criteria]) && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <LabelInput>Search using keywords</LabelInput>

              <EmptyDivElement
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  marginTop: '12px',
                  marginBottom: '12px',
                }}
                onClick={() => onClickClearAll(criteria)}
              >
                <LabelInput color="red">Clear All</LabelInput>
              </EmptyDivElement>
            </div>

            <InputText
              value={localSearch}
              onChange={e => handleDegreeSearch(e.target.value)}
              disabled={disabled}
            />
            <TableContainer>
              <StyledTable>
                <StyledThead>
                  <tr>
                    <StyledTh style={{ width: '10%' }}>
                      <FlexCell>
                        <TableCheckbox
                          checked={allDegrees}
                          indeterminate={degreesIndeterminate}
                          onChange={e => handleAllDegrees(e.target.checked)}
                          disabled={disabled}
                        />
                        <LabelText>DEGREE</LabelText>
                      </FlexCell>
                    </StyledTh>
                    <StyledTh style={{ width: '45%' }}>
                      <FlexCell>
                        <TableCheckbox
                          checked={allStreams}
                          indeterminate={streamsIndeterminate}
                          onChange={e => handleAllDepartments(e.target.checked)}
                          disabled={disabled}
                        />
                        <LabelText>DEPARTMENT</LabelText>
                      </FlexCell>
                    </StyledTh>
                    <StyledTh style={{ width: '45%' }}>
                      <FlexCell>
                        <TableCheckbox
                          checked={allSpecs}
                          indeterminate={specsIndeterminate}
                          onChange={e =>
                            handleAllSpecializations(e.target.checked)
                          }
                          disabled={disabled || localSearch?.length < 2}
                        />
                        <LabelText>SPECIALIZATION</LabelText>
                      </FlexCell>
                    </StyledTh>
                  </tr>
                </StyledThead>
                <tbody>
                  {degreeLoading ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center' }}>
                        No data found
                      </td>
                    </tr>
                  ) : (
                    data.map(degree => (
                      <React.Fragment key={degree.degreeId}>
                        <tr>
                          <StyledTd>
                            <FlexCell>
                              <TableCheckbox
                                checked={isDegreeSelected(degree.degreeId)}
                                onChange={e =>
                                  handleDegreeChange(degree, e.target.checked)
                                }
                                name={`elgibilty.eligibilityCriteria[${criteria}]`}
                                disabled={disabled}
                              />

                              <LabelText>{degree.degreeShortForm}</LabelText>
                              <ExpandIcon
                                onClick={() =>
                                  toggleExpand(
                                    setExpandedDegrees,
                                    degree.degreeId
                                  )
                                }
                              >
                                {expandedDegrees.includes(degree.degreeId) ? (
                                  <DownOutlined />
                                ) : (
                                  <RightOutlined />
                                )}
                              </ExpandIcon>
                            </FlexCell>
                          </StyledTd>
                          <StyledTd />
                          <StyledTd />
                        </tr>
                        {expandedDegrees.includes(degree.degreeId) &&
                          (degree.streams || []).map(stream => (
                            <React.Fragment key={stream.streamId}>
                              <tr>
                                <IndentedTd>
                                  <LabelText>
                                    {degree.degreeShortForm}
                                  </LabelText>
                                </IndentedTd>
                                <StyledTd>
                                  <FlexCell>
                                    <TableCheckbox
                                      checked={isStreamSelected(
                                        degree.degreeId,
                                        stream.streamId
                                      )}
                                      name={`elgibilty.eligibilityCriteria[${criteria}]`}
                                      onChange={() =>
                                        handleStreamChange(
                                          degree.degreeId,
                                          stream.streamId
                                        )
                                      }
                                      disabled={disabled}
                                    />
                                    <LabelText>{stream.streamName}</LabelText>
                                    {getSpecs(stream).length > 0 && (
                                      <ExpandIcon
                                        onClick={() =>
                                          toggleExpand(
                                            setExpandedStreams,
                                            stream.streamId
                                          )
                                        }
                                      >
                                        {expandedStreams.includes(
                                          stream.streamId
                                        ) ? (
                                          <DownOutlined />
                                        ) : (
                                          <RightOutlined />
                                        )}
                                      </ExpandIcon>
                                    )}
                                  </FlexCell>
                                </StyledTd>
                                <StyledTd />
                              </tr>

                              {expandedStreams.includes(stream.streamId) &&
                                getSpecs(stream).map((spec, index) => (
                                  <tr
                                    key={`${
                                      spec.id || spec.specialisationId || index
                                    }-spec`}
                                  >
                                    <IndentedTd>
                                      <LabelText>
                                        {degree.degreeShortForm}
                                      </LabelText>
                                    </IndentedTd>
                                    <IndentedTd>
                                      <LabelText>{stream.streamName}</LabelText>
                                    </IndentedTd>
                                    <StyledTd>
                                      <FlexCell>
                                        <TableCheckbox
                                          checked={isSpecSelected(
                                            degree.degreeId,
                                            stream.streamId,
                                            spec.id || spec.specialisationId
                                          )}
                                          name={`elgibilty.eligibilityCriteria[${criteria}]`}
                                          onChange={() =>
                                            handleSpecChange(
                                              degree.degreeId,
                                              stream.streamId,
                                              spec.id || spec.specialisationId,
                                              stream
                                            )
                                          }
                                          disabled={disabled}
                                        />
                                        <LabelText>
                                          {spec.specialisationName}
                                        </LabelText>
                                      </FlexCell>
                                    </StyledTd>
                                  </tr>
                                ))}
                            </React.Fragment>
                          ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </StyledTable>
            </TableContainer>
          </>
        )}
      </>
    )
  }
)

export default CourseTable
